import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const QuestionReview = ({ 
  quiz, 
  results, 
  currentQuestionIndex, 
  setCurrentQuestionIndex, 
  getQuestionById 
}) => {
  if (!quiz?.questions || !results?.feedback) {
    return (
      <div className="text-center py-4">
        <p className="quiz-description-modern">No questions available for review.</p>
      </div>
    );
  }

  const questions = quiz.questions;
  const currentQuestion = questions[currentQuestionIndex];
  const feedback = results.feedback[currentQuestion?.question_id];

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
  };

  return (
    <div className="question-review-section">
      <h5 className="mb-3 text-light">Detailed Question Review</h5>
      <p className="quiz-description-modern mb-4">
        Review each question to understand your mistakes and learn from correct answers.
      </p>

      {/* Question Navigation */}
      <div className="question-navigation mb-4">
        <div className="question-numbers-nav mb-4">
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            {questions.map((question, index) => {
              const questionFeedback = results.feedback[question.question_id];
              const isCorrect = questionFeedback?.correct;
              const isActive = index === currentQuestionIndex;
              
              return (
                <button
                  key={question.question_id}
                  className={`question-number-btn ${
                    isActive ? 'active' : ''
                  } ${isCorrect ? 'correct' : 'incorrect'}`}
                  onClick={() => goToQuestion(index)}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="navigation-controls mt-3">
          <button 
            className="btn-modern btn-secondary-modern me-2"
            onClick={goToPreviousQuestion}
            disabled={currentQuestionIndex === 0}
          >
            ← Previous
          </button>
          <span className="question-counter text-light mx-3">
            Question {currentQuestionIndex + 1} of {questions.length}
          </span>
          <button 
            className="btn-modern btn-secondary-modern ms-2"
            onClick={goToNextQuestion}
            disabled={currentQuestionIndex === questions.length - 1}
          >
            Next →
          </button>
        </div>
      </div>

      {/* Current Question Display */}
      {currentQuestion && feedback && (
        <div className="question-card">
          <div className="question-header">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-light mb-0">Question {currentQuestionIndex + 1}</h6>
              <span className={`badge-modern ${feedback.correct ? 'success' : 'danger'}`}>
                {feedback.correct ? 'Correct' : 'Incorrect'}
              </span>
            </div>
          </div>

          <div className="question-content">
            <div className="question-text mb-3">
              <p className="text-light">{currentQuestion.question_text}</p>
              {currentQuestion.code_snippet && (
                <div className="code-snippet-container">
                  <SyntaxHighlighter 
                    language="javascript" 
                    style={atomDark}
                    className="code-snippet rounded"
                    wrapLines={true}
                    showLineNumbers={true}
                  >
                    {currentQuestion.code_snippet}
                  </SyntaxHighlighter>
                </div>
              )}
            </div>

            <div className="answers-section">
              <div className="row">
                <div className="col-md-6">
                  <div className="answer-block">
                    <h6 className="text-light mb-2">Your Answer:</h6>
                    <div className={`answer-display ${feedback.correct ? 'correct-answer' : 'wrong-answer'}`}>
                      {Array.isArray(feedback.user_answer) 
                        ? feedback.user_answer.join(', ') 
                        : feedback.user_answer || 'No answer provided'}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="answer-block">
                    <h6 className="text-light mb-2">Correct Answer:</h6>
                    <div className="answer-display correct-answer">
                      {Array.isArray(currentQuestion.correct_answer) 
                        ? currentQuestion.correct_answer.join(', ') 
                        : currentQuestion.correct_answer}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {currentQuestion.explanation && (
              <div className="explanation-section">
                <h6 className="text-light mb-2">Explanation:</h6>
                <p className="quiz-description-modern">{currentQuestion.explanation}</p>
              </div>
            )}

            {(currentQuestion.difficulty || currentQuestion.patterns_tested) && (
              <div className="question-meta mt-3">
                <div className="meta-section">
                  {currentQuestion.difficulty && (
                    <div className="meta-item">
                      <span className="meta-label">Difficulty:</span>
                      <span className={`difficulty-badge ${currentQuestion.difficulty.toLowerCase()}`}>
                        {currentQuestion.difficulty.charAt(0).toUpperCase() + currentQuestion.difficulty.slice(1)}
                      </span>
                    </div>
                  )}
                  
                  {currentQuestion.patterns_tested && (
                    <div className="meta-item">
                      <span className="meta-label">Topics/Patterns:</span>
                      <div className="patterns-container">
                        {Array.isArray(currentQuestion.patterns_tested) 
                          ? currentQuestion.patterns_tested.map((pattern, index) => (
                              <span key={index} className="pattern-badge">
                                {pattern}
                              </span>
                            ))
                          : currentQuestion.patterns_tested.split(',').map((pattern, index) => (
                              <span key={index} className="pattern-badge">
                                {pattern.trim()}
                              </span>
                            ))
                        }
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionReview;
